import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  RefreshCw,
  Calendar,
  Users,
  FileText,
  Activity,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";

const StatisticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - 30);
    return {
      start: startDate.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0],
    };
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [animatedValues, setAnimatedValues] = useState({
    citasSolicitadas: 0,
    citasAsignadas: 0,
    totalUsuarios: 0,
    ofertasEmpleo: 0,
  });

  // Colores
  const colors = [
    "#1a5d33",
    "#a5294a",
    "#4158a8",
    "#2d7a47",
    "#3ea55c",
    "#5cb570",
  ];

  // WARNING: este enumerador de colores es estático y no cambia con el tiempo
  // TODO: si se quiere que cambie con el tiempo, se debe implementar una lógica para actualizarlo
  const entityColors = {
    ERACIS: "#1a5d33",
    "Servicios sociales": "#4158a8",
    SAE: "#a5294a",
    "Andalucía Orienta": "#0f7a5c",
    "Punto Vuela": "#e59819",
    "Oficina de vivienda": "#7c3aed",
  };

  /**
   * los estados de las citas y ciudadanos, y los tipos de servicios son estados estáticos
   * que no cambian con el tiempo, por lo que se definen aquí como constantes
   */
  const statusColors = {
    // Colores de estados de citas
    Pendiente: "rgba(229, 152, 25, 0.8)",
    Aceptado: "rgba(15, 122, 92, 0.8)", 
    Asignado: "rgba(35, 110, 122, 0.8)",
    Rechazado: "rgba(229, 62, 62, 0.8)",

    // Colores de estados de ciudadanos
    Activo: "rgba(15, 122, 92, 0.8)",
    // Aceptado: "rgba(51, 102, 204, 0.8)", // ya usado en citas
    // Pendiente: "rgba(229, 152, 25, 0.8)", // ya usado en citas
    // Rechazado: "rgba(229, 62, 62, 0.8)",  // ya usado en citas

    // Colores de tipos de servicios
    Empleo: "rgba(15, 122, 92, 0.8)", // Cambiado al mismo verde que "Activo"
    Formación: "rgba(165, 41, 74, 0.8)",
    Social: "rgba(65, 88, 168, 0.8)",
    Otros: "rgba(160, 160, 160, 0.8)", // Añadido un color para "Otros"
  };
  const defaultColor = "rgba(160, 180, 180, 0.8)"; // Color por defecto para tipos de servicio no definidos

  const roundNumber = (value, decimals) => {
    if( typeof value !== "number" || isNaN(value) || value === Infinity) return value;
    if(!decimals) decimals = 1; // Default to 2 decimal places
    return Number(Math.round(value + "e" + decimals) + "e-" + decimals);
  };

  // Datos de ejemplo
  const mockData = {
    lastUpdated: new Date().toISOString(),
    period: {
      start: dateRange.start,
      end: dateRange.end,
    },
    indicators: {
      citasPorServicio: {
        title: "Volumen de Solicitudes de Cita por Servicio",
        total: 0,
        data: [
          { tipoServicio: "Empleo", count: 0, percentage: 0 },
          { tipoServicio: "Formación", count: 0, percentage: 0 },
          { tipoServicio: "Social", count: 0, percentage: 0 },
        ],
      },
      estadoCitas: {
        title: "Estado de las Citas",
        total: 0,
        data: [
          { estado: "Pendiente", count: 0, percentage: 0 },
          { estado: "Agendada", count: 0, percentage: 0 },
          { estado: "Rechazada", count: 0, percentage: 0 },
          { estado: "Asignada", count: 0, percentage: 0 },
        ],
      },
      citasPorEntidad: {
        title: "Citas Asignadas por Entidad",
        total: 0,
        data: [
          { entidad: "Servicios sociales", count: 0, percentage: 0 },
          { entidad: "SAE", count: 0, percentage: 0 },
          { entidad: "Andalucía Orienta", count: 0, percentage: 0 },
          { entidad: "Oficina de vivienda", count: 0, percentage: 0 },
        ],
      },
      ciudadanosPorEstado: {
        title: "Usuarios por Estado",
        total: 0,
        data: [
          { estado: "Activo", count: 0, percentage: 0 },
          { estado: "Aceptado", count: 0, percentage: 0 },
          { estado: "Pendiente", count: 0, percentage: 0 },
          { estado: "Rechazado", count: 0, percentage: 0 },
        ],
      },
      actividadContenidos: {
        title: "Actividad de Contenidos",
        data: {
          ofertasEmpleo: { periodo: 0, total: 0 },
          cursos: { periodo: 0, total: 0 },
          programasSociales: { periodo: 0, total: 0 },
        },
      },
    },
  };

  const animateCounters = (targetData) => {
    const targets = {
      citasSolicitadas: targetData.indicators.citasPorServicio.total,
      citasAsignadas: targetData.indicators.citasPorEntidad.total,
      totalUsuarios: targetData.indicators.ciudadanosPorEstado.total,
      ofertasEmpleo:
        targetData.indicators.actividadContenidos.data.ofertasEmpleo.total,
    };

    setIsAnimating(true);
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedValues({
        citasSolicitadas: Math.floor(targets.citasSolicitadas * easeOut),
        citasAsignadas: Math.floor(targets.citasAsignadas * easeOut),
        totalUsuarios: Math.floor(targets.totalUsuarios * easeOut),
        ofertasEmpleo: Math.floor(targets.ofertasEmpleo * easeOut),
      });

      if (currentStep >= steps) {
        setAnimatedValues(targets);
        setIsAnimating(false);
        clearInterval(interval);
      }
    }, stepDuration);
  };

  const fetchStatistics = async () => {
    try{
      setLoading(true);
      setData(null);
      // Fectch from API: GET {{base_url}}/statistics with Bearer token from localStorage
      const queryParams = new URLSearchParams({
        start: dateRange.start,
        end: dateRange.end
      });
      
      const response = await fetch(`${API_BASE_URL}/statistics?${queryParams}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok) throw new Error(data.message || "Error al cargar las estadísticas");

      const payload = await response.json();
      if (!payload || !payload.isOk) throw new Error("Error en la respuesta del servidor");

      setData(payload.data);
      setLastUpdated(new Date(payload.data.lastUpdated));
      animateCounters(payload.data);
      setLoading(false);
    }
    catch (error) {
      console.error("Error al obtener las estadísticas:", error);
      setError(error.message || "Error al cargar las estadísticas");
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch statistics on component mount
    fetchStatistics();
  }, []);

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatDateForDisplay = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleDateRangeChange = (newStart, newEnd) => {
    setDateRange({ start: newStart, end: newEnd });
    setShowDatePicker(false);
  };

  const StatCard = ({
    title,
    value,
    icon: Icon,
    animatedValue,
    isAnimating,
  }) => (
    <div
      className={`bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 border-l-4 hover:shadow-xl transition-all duration-500 transform ${
        isAnimating ? "animate-pulse scale-105" : "hover:-translate-y-1"
      }`}
      style={{ borderLeftColor: "#1a5d33" }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
          <p
            className={`text-4xl font-bold text-gray-900 transition-all duration-300 ${
              isAnimating ? "text-green-600" : ""
            }`}
          >
            {(animatedValue || 0).toLocaleString()}
          </p>
        </div>
        <div
          className={`bg-gradient-to-br from-green-700 to-green-800 p-4 rounded-xl transition-transform duration-300 ${
            isAnimating ? "animate-bounce" : ""
          }`}
        >
          <Icon className="h-10 w-10 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-lg text-gray-600">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No se pudieron cargar los datos
          </h2>
          <button
            onClick={fetchStatistics}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const { indicators } = data;

  return (
    <div className="min-h-screen min-w-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-green-700 via-green-800 to-green-900 rounded-xl shadow-lg p-8 text-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 p-3 rounded-lg">
                  <Activity className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold mb-2">Cuadro de Mando</h1>
                  <p className="text-green-100 text-lg">
                    Pasaporte hacia el Empleo - Isla Cristina
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <div className="text-right bg-white/10 p-4 rounded-lg">
                  <p className="text-green-200 text-sm">Última actualización</p>
                  <p className="text-white font-semibold">
                    {lastUpdated ? formatDate(lastUpdated) : "N/A"}
                  </p>
                </div>
                <button
                  onClick={fetchStatistics}
                  className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-lg transition-all duration-200 transform hover:scale-105"
                  title="Actualizar datos"
                >
                  <RefreshCw className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="mt-6 bg-white/10 p-4 rounded-lg relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center space-x-3 w-full text-left hover:bg-white/5 p-2 rounded transition-colors"
              >
                <Calendar className="h-5 w-5 text-green-200" />
                <span className="text-green-100 font-medium">
                  Período de análisis:
                </span>
                <span className="text-white font-semibold">
                  {formatDateForDisplay(dateRange.start)} -{" "}
                  {formatDateForDisplay(dateRange.end)}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-green-200 transition-transform ${
                    showDatePicker ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showDatePicker && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg p-4 z-50 min-w-80">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha de inicio
                      </label>
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) =>
                          setDateRange((prev) => ({
                            ...prev,
                            start: e.target.value,
                          }))
                        }
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-black focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha de fin
                      </label>
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) =>
                          setDateRange((prev) => ({
                            ...prev,
                            end: e.target.value,
                          }))
                        }
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-black focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() =>
                        handleDateRangeChange(dateRange.start, dateRange.end)
                      }
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Indicadores Principales */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <StatCard
            title="Total Citas Solicitadas"
            value={indicators.citasPorServicio.total}
            animatedValue={animatedValues.citasSolicitadas}
            icon={Calendar}
            isAnimating={isAnimating}
          />
          <StatCard
            title="Citas Asignadas"
            value={indicators.citasPorEntidad.total}
            animatedValue={animatedValues.citasAsignadas}
            icon={FileText}
            isAnimating={isAnimating}
          />
          <StatCard
            title="Total Usuarios"
            value={indicators.ciudadanosPorEstado.total}
            animatedValue={animatedValues.totalUsuarios}
            icon={Users}
            isAnimating={isAnimating}
          />
          <StatCard
            title="Ofertas de Empleo"
            value={indicators.actividadContenidos.data.ofertasEmpleo.total}
            animatedValue={animatedValues.ofertasEmpleo}
            icon={Activity}
            isAnimating={isAnimating}
          />
        </div>

        {/* Gráficos primera fila */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 transition-all duration-700 ${
            isAnimating ? "opacity-50 scale-95" : "opacity-100 scale-100"
          }`}
        >
          {/* Citas por Servicio */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6 border-b border-gray-100 pb-3">
              {indicators.citasPorServicio.title}
            </h3>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={indicators.citasPorServicio.data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ tipoServicio, percentage }) =>
                    `${tipoServicio}\n(${roundNumber(percentage)}%)`
                  }
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="tipoServicio"
                  animationBegin={0}
                  animationDuration={1500}
                >
                  {indicators.citasPorServicio.data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={statusColors[entry.tipoServicio]||defaultColor}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Estado de Citas */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6 border-b border-gray-100 pb-3">
              {indicators.estadoCitas.title}
            </h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={indicators.estadoCitas.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis dataKey="estado" />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="count"
                  radius={[4, 4, 0, 0]}
                  animationBegin={300}
                  animationDuration={1200}
                >
                  {indicators.estadoCitas.data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={statusColors[entry.estado]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Usuarios por Estado */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6 border-b border-gray-100 pb-3">
              {indicators.ciudadanosPorEstado.title}
            </h3>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={indicators.ciudadanosPorEstado.data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ estado, percentage }) =>
                    `${estado}\n(${roundNumber(percentage)}%)`
                  }
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="estado"
                  animationBegin={600}
                  animationDuration={1500}
                >
                  {indicators.ciudadanosPorEstado.data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={statusColors[entry.estado]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Segunda fila */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 transition-all duration-700 ${
            isAnimating ? "opacity-50 scale-95" : "opacity-100 scale-100"
          }`}
        >
          {/* Citas por Entidad */}
          <div className="lg:col-span-2 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6 border-b border-gray-100 pb-3">
              {indicators.citasPorEntidad.title}
            </h3>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={indicators.citasPorEntidad.data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.1)"
                  />
                  <XAxis
                    dataKey="entidad"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    fontSize={10}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    radius={[4, 4, 0, 0]}
                    animationBegin={900}
                    animationDuration={1500}
                  >
                    {indicators.citasPorEntidad.data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entityColors[entry.entidad] ||
                          colors[index % colors.length]
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Actividad de Contenidos */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6 border-b border-gray-100 pb-3">
              {indicators.actividadContenidos.title}
            </h3>
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-green-50/80 to-green-100/80 backdrop-blur-sm p-6 rounded-xl border border-green-200/50">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-green-900">
                    Ofertas de Empleo
                  </h4>
                  <FileText className="h-6 w-6 text-green-700" />
                </div>
                <p className="text-3xl font-bold text-green-800 mb-1">
                  {isAnimating
                    ? animatedValues.ofertasEmpleo
                    : indicators.actividadContenidos.data.ofertasEmpleo.total}
                </p>
                <p className="text-sm text-green-700">
                  +{indicators.actividadContenidos.data.ofertasEmpleo.periodo}{" "}
                  en período
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50/80 to-pink-100/80 backdrop-blur-sm p-6 rounded-xl border border-pink-200/50">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-pink-900">Cursos</h4>
                  <Users className="h-6 w-6" style={{ color: "#a5294a" }} />
                </div>
                <p
                  className="text-3xl font-bold mb-1"
                  style={{ color: "#a5294a" }}
                >
                  {indicators.actividadContenidos.data.cursos.total}
                </p>
                <p className="text-sm" style={{ color: "#b8395a" }}>
                  +{indicators.actividadContenidos.data.cursos.periodo} en
                  período
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50/80 to-blue-100/80 backdrop-blur-sm p-6 rounded-xl border border-blue-200/50">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-blue-900">
                    Programas Sociales
                  </h4>
                  <Activity className="h-6 w-6" style={{ color: "#4158a8" }} />
                </div>
                <p
                  className="text-3xl font-bold mb-1"
                  style={{ color: "#4158a8" }}
                >
                  {indicators.actividadContenidos.data.programasSociales.total}
                </p>
                <p className="text-sm" style={{ color: "#5068b8" }}>
                  +
                  {
                    indicators.actividadContenidos.data.programasSociales
                      .periodo
                  }{" "}
                  en período
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsDashboard;
